import { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Loader2, Check, X, Sparkles, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Dialog di edit allergeni HACCP per un singolo prodotto (ingrediente food).
 * - Mostra metadata 14 allergeni EU con icone+label
 * - 2 zone: "Contiene" (selezionati) + "Aggiungi altri"
 * - Pulsanti: "Ri-analizza con AI" / "Conferma"
 */
const EditAllergeniDialog = ({ prod, open, onClose, onSaved }) => {
  const { token } = useAuth();
  const [metadata, setMetadata] = useState([]);
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);
  const [reanalyzing, setReanalyzing] = useState(false);
  const [confidence, setConfidence] = useState(null);

  const authHeaders = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!open || !token) return;
    fetch(`${API}/api/allergeni/metadata`, { headers: authHeaders })
      .then((r) => r.json())
      .then(setMetadata)
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, token]);

  useEffect(() => {
    if (prod) {
      setSelected([...(prod.allergeni || [])]);
      setConfidence(prod.allergeni_confidence ?? null);
    }
  }, [prod]);

  if (!prod) return null;

  const toggle = (key) => {
    setSelected((prev) => (prev.includes(key) ? prev.filter((a) => a !== key) : [...prev, key]));
  };

  const handleReanalyze = async () => {
    setReanalyzing(true);
    try {
      const res = await fetch(`${API}/api/ingredienti/${prod.id}/auto-allergeni`, {
        method: "POST",
        headers: authHeaders,
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || "Errore");
        return;
      }
      setSelected([...(data.allergeni || [])]);
      setConfidence(data.confidence ?? null);
      toast.success("✨ Allergeni rianalizzati con AI");
    } finally {
      setReanalyzing(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/ingredienti/${prod.id}/allergeni`, {
        method: "PUT",
        headers: { ...authHeaders, "Content-Type": "application/json" },
        body: JSON.stringify({ allergeni: selected }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Errore");
        return;
      }
      toast.success("Allergeni aggiornati");
      if (onSaved) onSaved();
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const attivi = metadata.filter((al) => selected.includes(al.key));
  const inattivi = metadata.filter((al) => !selected.includes(al.key));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl" data-testid="edit-allergeni-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" />
            Allergeni — {prod.nome}
          </DialogTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Reg. UE 1169/2011 · clicca le pillole per togliere o aggiungere
          </p>
        </DialogHeader>

        <div className="space-y-3">
          {/* Stato attuale */}
          <div className="flex items-center gap-2 flex-wrap">
            {prod.allergeni_verified && (
              <span className="text-[11px] font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200 flex items-center gap-1">
                <ShieldCheck className="h-3 w-3" /> Confermato
              </span>
            )}
            {!prod.allergeni_verified && prod.allergeni_source && (
              <span className="text-[11px] font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200">
                Da confermare
              </span>
            )}
            {confidence !== null && (
              <span className="text-[10px] text-muted-foreground">
                AI confidence: {(confidence * 100).toFixed(0)}%
              </span>
            )}
          </div>

          {/* Zona 1: Contiene */}
          <div>
            <p className="text-[10px] uppercase tracking-wider font-bold text-rose-600 mb-1.5">
              {attivi.length > 0 ? `Contiene (${attivi.length})` : "Nessun allergene"}
            </p>
            {attivi.length === 0 ? (
              <p className="text-xs text-muted-foreground italic">Aggiungi allergeni dalla lista sotto</p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {attivi.map((al) => (
                  <button
                    key={al.key}
                    type="button"
                    onClick={() => toggle(al.key)}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border-2 border-rose-500 bg-rose-100 text-rose-900 text-[11px] font-bold shadow-sm hover:bg-rose-200 transition-all"
                    title="Clicca per togliere"
                    data-testid={`edit-pill-active-${al.key}`}
                  >
                    <span>{al.icon}</span>
                    <span>{al.label}</span>
                    <X className="h-3 w-3 ml-0.5" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Zona 2: Aggiungi */}
          {inattivi.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-1.5">
                {attivi.length > 0 ? "Aggiungi altri" : "Tocca per aggiungere"}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {inattivi.map((al) => (
                  <button
                    key={al.key}
                    type="button"
                    onClick={() => toggle(al.key)}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-slate-200 bg-slate-50 text-slate-500 text-[11px] hover:bg-slate-100 hover:border-slate-300 transition-all"
                    data-testid={`edit-pill-inactive-${al.key}`}
                  >
                    <span className="opacity-60">{al.icon}</span>
                    <span>{al.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleReanalyze}
            disabled={reanalyzing || saving}
            className="border-amber-400 text-amber-800 hover:bg-amber-50"
            data-testid="btn-edit-reanalize"
          >
            {reanalyzing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="h-4 w-4 mr-1.5" /> Ri-analizza con AI</>}
          </Button>
          <div className="flex-1" />
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>
            Annulla
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="btn-edit-conferma"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4 mr-1.5" /> Salva e conferma</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditAllergeniDialog;
